import { ITableFilters } from '@interfaces';
import { IItemsWithCount, ILeadEntity } from '@rasayi-workspace/shared';
import { EMPTY_INITIAL_ITEMS } from '@services';
import { createContext, ReactNode, useContext, useState } from 'react';

interface IData {
    id: string;
    name: string;
}
export interface IBrand {
    id: string;
    name: string;
    charges: number;
}
export interface IApr {
    id: string;
    amount: number;
    rate: number;
}
export interface IInstallmentPlans {
    id: string;
    name: string;
    cycle: number;
}
export interface IDate {
    startDate: string;
    endDate: string;
}

export interface ILeadContext {
    leadsData: IItemsWithCount<ILeadEntity>;
    holdLeadsData: IItemsWithCount<ILeadEntity>;
    updatingLeadEntity: ILeadEntity | null;
    channels: IData[];
    agents: IData[];
    brands: IBrand[];
    productType: IData[];
    installmentPlans: IInstallmentPlans[];
    aprs: IApr[];
    date: IDate;

    leadsFiltars: ITableFilters;
    holdLeadsFiltars: ITableFilters;
    dropofLeadsFiltars: ITableFilters;
    leadsGuarantorsFiltars: ITableFilters;
    creditFiltars: ITableFilters;
    repeatedCreditFilters: ITableFilters;
    approvedLeadFilters: ITableFilters;
    rejectedLeadFilters: ITableFilters;

    setLeadsData: (leads: IItemsWithCount<ILeadEntity>) => void;
    setHoldLeadsData: (leads: IItemsWithCount<ILeadEntity>) => void;
    setChannels: (channels: IData[]) => void;
    setAgents: (agents: IData[]) => void;
    setBrands: (brands: IBrand[]) => void;
    setProductType: (productType: IData[]) => void;
    setInstallmentPlans: (installmentPlans: IInstallmentPlans[]) => void;
    setAprs: (apr: IApr[]) => void;
    setUpdatingLeadEntity: (lead: ILeadEntity | null) => void;
    setDate: (date: IDate) => void;
    setLeadsFiltars: (date: ITableFilters) => void;
    setHoldLeadsFiltars: (date: ITableFilters) => void;
    setDropofLeadsFiltars: (date: ITableFilters) => void;
    setLeadsGuarantorsFiltars: (date: ITableFilters) => void;
    setCreditFiltars: (date: ITableFilters) => void;
    setRepeatedCreditFilters: (date: ITableFilters) => void;
    setApprovedLeadFilters: (date: ITableFilters) => void;
    setRejectedLeadFilters: (date: ITableFilters) => void;
}

const LeadContext = createContext<ILeadContext | undefined>(undefined);

const EMPTY_FILTERS_ITEM: ITableFilters = {
    agent: [],
    status: [],
    city: [],
    segment: [],
    productType: [],
    channel: [],
    quantity: null,
    brand: [],
    createdAt: null,
    searchedText: '',
    province: [],
    customerStatus: [],
};

export const LeadContextProvider = ({ children }: { children: ReactNode }) => {
    const [leadsData, setLeadsData] = useState<IItemsWithCount<ILeadEntity>>(EMPTY_INITIAL_ITEMS);
    const [holdLeadsData, setHoldLeadsData] = useState<IItemsWithCount<ILeadEntity>>(EMPTY_INITIAL_ITEMS);

    const [updatingLeadEntity, setUpdatingLeadEntity] = useState<ILeadEntity | null>(null);

    const [channels, setChannels] = useState<IData[]>([]);
    const [agents, setAgents] = useState<IData[]>([]);
    const [brands, setBrands] = useState<IBrand[]>([]);
    const [productType, setProductType] = useState<IData[]>([]);
    const [installmentPlans, setInstallmentPlans] = useState<IInstallmentPlans[]>([]);
    const [aprs, setAprs] = useState<IApr[]>([]);
    const [date, setDate] = useState<IDate>({ startDate: '', endDate: '' });

    const [leadsFiltars, setLeadsFiltars] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [holdLeadsFiltars, setHoldLeadsFiltars] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [dropofLeadsFiltars, setDropofLeadsFiltars] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [leadsGuarantorsFiltars, setLeadsGuarantorsFiltars] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);

    const [creditFiltars, setCreditFiltars] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [repeatedCreditFilters, setRepeatedCreditFilters] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [approvedLeadFilters, setApprovedLeadFilters] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);
    const [rejectedLeadFilters, setRejectedLeadFilters] = useState<ITableFilters>(EMPTY_FILTERS_ITEM);

    return (
        <LeadContext.Provider
            value={{
                date,
                setDate,
                leadsData,
                setLeadsData,
                holdLeadsData,
                setHoldLeadsData,
                channels,
                setChannels,
                agents,
                setAgents,
                brands,
                setBrands,
                productType,
                setProductType,
                installmentPlans,
                setInstallmentPlans,
                aprs,
                setAprs,
                updatingLeadEntity,
                setUpdatingLeadEntity,
                leadsFiltars,
                setLeadsFiltars,
                holdLeadsFiltars,
                setHoldLeadsFiltars,
                dropofLeadsFiltars,
                setDropofLeadsFiltars,
                leadsGuarantorsFiltars,
                setLeadsGuarantorsFiltars,
                creditFiltars,
                setCreditFiltars,
                repeatedCreditFilters,
                setRepeatedCreditFilters,
                approvedLeadFilters,
                setApprovedLeadFilters,
                rejectedLeadFilters,
                setRejectedLeadFilters,
            }}
        >
            {children}
        </LeadContext.Provider>
    );
};

export const useLeadContext = () => {
    const context = useContext(LeadContext);
    if (!context) {
        throw new Error('useLeadContext must be used within a LeadProvider');
    }
    return context;
};
